import { useState } from "react";
import { Button, Loader, Modal } from "@src/components/atoms";
import AdminHooks from "@src/context/hooks/adminHooks";
import { useAuthContext } from "@src/context";

const EMPTY_GROUP = { name: "", groupCode: "" };

const GroupForm = ({ initial = EMPTY_GROUP, onSave, onCancel, isNew }) => {
  const [form, setForm] = useState({ ...EMPTY_GROUP, ...initial });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.name || !form.groupCode) {
      setError("Name and group code are required.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onSave(form);
    } catch {
      setError("Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 p-4">
      <h4 className="font-bold text-primary-500 uppercase">
        {isNew ? "New Behaviour Group" : "Edit Behaviour Group"}
      </h4>
      {!isNew && <div className="text-xs text-gray-400">id: {initial.id}</div>}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500">Name *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            value={form.name}
            onChange={set("name")}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Group Code *</label>
          <input
            className="w-full border border-neutral-200 rounded p-2 text-sm"
            value={form.groupCode}
            onChange={set("groupCode")}
          />
        </div>
      </div>
      {error && <div className="text-xs text-red-500">{error}</div>}
      <div className="flex gap-2 pt-2">
        <Button submit={false} loading={submitting} onClick={handleSave}>
          Save
        </Button>
        <Button secondary onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export const BehaviourGroupsSection = () => {
  const auth = useAuthContext();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const {
    data: groups = [],
    isLoading,
    isError,
  } = AdminHooks.useGetBehaviourGroups(auth.activeRole);

  const createMutation = AdminHooks.useCreateBehaviourGroup();
  const updateMutation = AdminHooks.useUpdateBehaviourGroup();
  const deleteMutation = AdminHooks.useDeleteBehaviourGroup();

  const handleDelete = async (group) => {
    if (
      !window.confirm(
        `Delete group "${group.name}" (${group.groupCode})? Behaviour types in this group will have their group unset, but will not be deleted.`,
      )
    )
      return;
    setError("");
    setSubmitted(false);
    try {
      await deleteMutation.mutateAsync(group.id);
      setSubmitted(true);
    } catch {
      setError("Failed to delete.");
    }
  };

  const handleSave = async (data) => {
    setError("");
    setSubmitted(false);
    if (adding) {
      await createMutation.mutateAsync(data);
    } else {
      await updateMutation.mutateAsync(data);
    }
    setAdding(false);
    setEditing(null);
    setSubmitted(true);
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between pb-3">
        <h3 className="font-bold text-lg">Global Behaviour Groups</h3>
        <Button
          icon="plus"
          block={false}
          onClick={() => setAdding(true)}
          secondary
        >
          Add group
        </Button>
      </div>

      {!!submitted && (
        <div className="mb-2 text-green-600 border border-green-300 rounded-xl p-2 bg-green-50 text-sm">
          Changes saved successfully.
        </div>
      )}
      {!!error && (
        <div className="mb-2 text-red-600 border border-red-300 rounded-xl p-2 bg-red-50 text-sm">
          {error}
        </div>
      )}

      {isLoading && <Loader size="2xl" />}
      {!isLoading && isError && (
        <div className="text-center text-red-500">
          Error loading behaviour groups.
        </div>
      )}

      {!isLoading && !isError && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-gray-500 uppercase">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Group Code</th>
                <th className="py-2 pr-4">ID</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr
                  key={group.id}
                  className="border-b border-neutral-100 hover:bg-neutral-50"
                >
                  <td className="py-2 pr-4 font-bold">{group.name}</td>
                  <td className="py-2 pr-4">{group.groupCode}</td>
                  <td className="py-2 pr-4 text-xs text-gray-400 font-mono">
                    {group.id}
                  </td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      <Button
                        icon="feedback"
                        block={false}
                        secondary
                        onClick={() => setEditing(group)}
                      >
                        Edit
                      </Button>
                      <Button
                        icon="close"
                        color="red"
                        block={false}
                        secondary
                        onClick={() => handleDelete(group)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-400">
                    No behaviour groups defined.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {(editing || adding) && (
        <Modal
          open={!!(editing || adding)}
          handleClose={() => {
            setEditing(null);
            setAdding(false);
          }}
        >
          <GroupForm
            initial={editing || EMPTY_GROUP}
            isNew={adding}
            onSave={handleSave}
            onCancel={() => {
              setEditing(null);
              setAdding(false);
            }}
          />
        </Modal>
      )}
    </div>
  );
};
